/**
 * AI Act Art. 50(2): machine-readable marking of content an AI wrote into a
 * document, a task or a board (deadline for systems already on the market:
 * 2 Dec 2026; see the Apiboo doc "AI Act — AI-generated content disclosure
 * (Art. 50)", § 5).
 *
 * The marks are plain keys on the record's `data`, next to the human
 * authorship fields (`createdBy`, `createdAt`), which they never replace:
 *
 *   ai_generated  true      the AI created the item (set once, on create)
 *   ai_edited_at  ISO       the AI last rewrote or appended to the content
 *   ai_source     'assistant' (the in-app assistant) | 'mcp' (an external agent)
 *   ai_provider   'apiboo' | 'anthropic' | 'openai' | 'gemini' | 'openrouter'
 *   ai_model      the model id, where it is known
 *   mcp_client    the MCP client's id ('claude', 'codex', …, or 'mcp')
 *   ai_sig        a short MinHash signature of the text as the AI left it
 *
 * `ai_sig` is what makes the visible label "clearable": when a person later
 * rewrites the content substantially, the signature no longer matches the
 * current text, the label stays and its tooltip says "edited since". Nothing
 * has to hook the human save paths (editor autosave, task modal, git sync).
 *
 * Pure and import-free on purpose: the MCP handlers import this, and their
 * whole import graph is pinned by `mcpAttackSurface.test.js`.
 */

/** Every key this module may write; copied forward by explicit-field records (boards). */
export const AI_MARK_KEYS = Object.freeze([
  'ai_generated',
  'ai_edited_at',
  'ai_source',
  'ai_provider',
  'ai_model',
  'mcp_client',
  'ai_sig',
]);

/** Hash functions in a signature: 16 × 8 hex = 128 chars on the record. */
const SIG_SIZE = 16;
/** Below this share of matching minima the content counts as rewritten. */
export const EDITED_SINCE_THRESHOLD = 0.6;

let activeTarget = null;

/**
 * The provider and model of the assistant turn now running (engine.runAiTurn
 * calls this once the target is resolved). On the Apiboo path the model id
 * sent is the 'apiboo' sentinel; the real one is the status' `model`.
 */
export function noteAiTurnTarget(target) {
  if (!target || !target.provider) {
    activeTarget = null;
    return;
  }
  const model = target.via === 'apiboo' || target.model === 'apiboo'
    ? target.apiboo?.model || null
    : target.model || null;
  activeTarget = { provider: String(target.provider), model: model ? String(model) : null };
}

/** `{ ai_provider, ai_model }` of the current assistant turn, unknown keys left out. */
export function assistantProvenance() {
  if (!activeTarget) return {};
  return {
    ai_provider: activeTarget.provider,
    ...(activeTarget.model ? { ai_model: activeTarget.model } : {}),
  };
}

/** Test seam. */
export function resetAiTurnTarget() {
  activeTarget = null;
}

const ENTITIES = { '&nbsp;': ' ', '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };

/** Plain text of stored rich-text HTML: tags are word breaks, a few entities decoded. */
export function htmlText(html) {
  return String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(nbsp|amp|lt|gt|quot|#39);/g, (m) => ENTITIES[m] || ' ');
}

function words(text) {
  return String(text || '').toLowerCase().match(/[\p{L}\p{N}]+/gu) || [];
}

function fnv1a(str, seed) {
  let h = (0x811c9dc5 ^ seed) >>> 0;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * MinHash over the text's set of words: two signatures agree in roughly the
 * share of distinct words the texts share (Jaccard), so fixing a typo or a
 * word keeps it close and a rewrite does not. Returns '' for an empty text.
 */
export function contentSignature(html) {
  const shingles = new Set(words(htmlText(html)));
  if (shingles.size === 0) return '';
  let out = '';
  for (let s = 0; s < SIG_SIZE; s += 1) {
    const seed = Math.imul(s + 1, 0x9e3779b1) >>> 0;
    let min = 0xffffffff;
    shingles.forEach((sh) => {
      const h = fnv1a(sh, seed);
      if (h < min) min = h;
    });
    out += min.toString(16).padStart(8, '0');
  }
  return out;
}

/** Share (0..1) of matching minima between two signatures. */
export function signatureSimilarity(a, b) {
  if (!a && !b) return 1;
  if (!a || !b || a.length !== b.length) return 0;
  let same = 0;
  for (let i = 0; i < a.length; i += 8) if (a.slice(i, i + 8) === b.slice(i, i + 8)) same += 1;
  return same / (a.length / 8);
}

/**
 * The source of an MCP write: the client's id as Rust read it from the wire
 * (`ctx.caller.agent`, client_agent.rs) — beside the tool arguments, never
 * from them, so a model cannot name itself. Anything unexpected is 'mcp'.
 */
export function mcpSource(caller) {
  const agent = caller && typeof caller === 'object' ? caller.agent : null;
  const client = typeof agent === 'string' && /^[a-z0-9_-]{1,32}$/i.test(agent) ? agent.toLowerCase() : null;
  return { mcp: true, client };
}

function sourceFields(source) {
  if (source && source.mcp) {
    return { ai_source: 'mcp', mcp_client: source.client ? String(source.client) : 'mcp' };
  }
  return { ai_source: 'assistant', ...assistantProvenance() };
}

/**
 * Marks for an item the AI CREATED. `source` is omitted for the in-app
 * assistant, `{ mcp: true, client }` for an external agent. `bodyHtml` is the
 * content as written (omit it for a board, which has no body).
 */
export function aiCreatedMarks(bodyHtml, source = null) {
  return {
    ai_generated: true,
    ...sourceFields(source),
    ...(bodyHtml !== undefined ? { ai_sig: contentSignature(bodyHtml) } : {}),
  };
}

/**
 * Marks for an EXISTING item whose content the AI just rewrote or appended
 * to. `bodyHtml` is the whole content after the write. `ai_generated` and the
 * human authorship fields are left as they are.
 */
export function aiEditedMarks(bodyHtml, source = null, now = new Date()) {
  return {
    ai_edited_at: now.toISOString(),
    ...sourceFields(source),
    ai_sig: contentSignature(bodyHtml),
  };
}

/** The AI keys present on `record`, for records that are rebuilt field by field. */
export function aiMarkFields(record) {
  const out = {};
  if (!record || typeof record !== 'object') return out;
  for (const key of AI_MARK_KEYS) {
    if (key === 'ai_generated') {
      if (record.ai_generated === true) out.ai_generated = true;
    } else if (typeof record[key] === 'string' && record[key]) {
      out[key] = record[key];
    }
  }
  return out;
}

/**
 * What the visible label says about `record` given its current content:
 * null (no AI mark), or `{ kind: 'created'|'edited', source, client,
 * editedSince }`. `editedSince` is true once the current text has drifted
 * substantially from the text the AI left (its `ai_sig`). A record marked
 * without a signature (before this existed) cannot tell, and says nothing.
 */
export function aiMarkState(record, currentHtml) {
  if (!record || (record.ai_generated !== true && !record.ai_edited_at)) return null;
  const kind = record.ai_edited_at ? 'edited' : 'created';
  let editedSince = false;
  if (typeof record.ai_sig === 'string' && currentHtml !== undefined && currentHtml !== null) {
    editedSince = signatureSimilarity(record.ai_sig, contentSignature(currentHtml)) < EDITED_SINCE_THRESHOLD;
  }
  return {
    kind,
    source: record.ai_source === 'mcp' ? 'mcp' : 'assistant',
    client: record.mcp_client || null,
    editedSince,
  };
}
